import React, { useEffect, useMemo, useState } from 'react';
import { MRT_ColumnDef, useMaterialReactTable, MRT_Row, MaterialReactTable } from 'material-react-table';
import { Box, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { defaultBooking, IBooking } from '../interfaces/Booking.interface';
import { useSetting } from '../contexts/SettingContext';
import PageviewIcon from '@mui/icons-material/Pageview';
import { useMembers } from '../contexts/MemberContext';
import DialogWithClose from './DialogWithClose';

interface EventDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    event: IBooking;
}

const BookingHistoryDialog: React.FC<EventDetailsDialogProps> = ({ open, onClose, event }) => {
    const { members } = useMembers();
    const [member, setMember] = useState(members.find(m => m.memberId == event.teacherId || event.studentIds?.includes(m.memberId))!);
    const [memberUiInfo, setMemberUiInfo] = useState<{ label: string; value: string; size: number; }[]>([]);
    const { settings } = useSetting();


    useEffect(() => {
        const currentMember = members.find(m => m.memberId == event.teacherId || event.studentIds?.includes(m.memberId));

        const services = event.serviceIds.map(serviceId => settings.services.find(s => s.serviceId === serviceId)!);
        if (currentMember) {
            setMember(currentMember);

            setMemberUiInfo([
                { label: 'Student', value: `${currentMember.firstName} ${currentMember.lastName}`, size: 6 },
                { label: 'Email', value: currentMember.email, size: 6 },
                { label: 'Phone', value: currentMember.phone, size: 6 },
                { label: 'Start Date and Time', value: toLocalDateTimeString(event.startTime), size: 12 },
                { label: 'Duration', value: event.duration!.toString(), size: 6 },
                { label: 'Reminder', value: event.description!, size: 12 },
                { label: 'Permanent Reminder', value: currentMember.note, size: 12 },
                { label: 'Attendance', value: event.attendance!, size: 6 },
                {
                    label: 'Services', value: event.serviceIds.map(sid => {
                        const service = services.find(s => s.serviceId === sid);
                        return service!.name;
                    }).join(", "), size: 12
                },
            ]);


        }
    }, [event])

    const toLocalDateTimeString = (date: Date) => {
        const curDate = new Date(date);
        const offset = curDate.getTimezoneOffset();
        const localDate = new Date(curDate.getTime() - offset * 60 * 1000);
        return localDate.toString();
    };



    return (
        <DialogWithClose open={open} onClose={onClose}>
        <DialogTitle>Booking Details</DialogTitle>
        {member && (
            <>
                <DialogContent>
                   <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Label</th>
                                <th style={{ borderBottom: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {memberUiInfo.map((info, index) => (
                                <tr key={index}>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{info.label}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{info.value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table> 
                </DialogContent>
            </>
        )}
    </DialogWithClose>
    
    );
};


interface BookingHistoryProps {
    data: IBooking[];
}
export const BookingHistory: React.FC<BookingHistoryProps> = ({ data }) => {
    const [isViewing, setIsViewing] = useState(false);
    const [currentRow, setCurrentRow] = useState<IBooking>(defaultBooking);
    const [tableData, setTableData] = useState<IBooking[]>(data);

    useEffect(() => {
        setTableData(data);
    }, [data]);

    const columns: Array<MRT_ColumnDef<IBooking>> = useMemo(() => {
        const actionColumn: MRT_ColumnDef<IBooking> = {
            header: '',
            id: 'actions',
            Cell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                    <Tooltip title="View">
                        <IconButton onClick={() => {
                            setCurrentRow(row.original);
                            setIsViewing(true);
                        }}>
                            <PageviewIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
            size: 100,
            minSize: 100,
            maxSize: 200,
        };

        const dynamicColumns: Array<MRT_ColumnDef<IBooking>> = [
            {
                header: 'Visit Date',
                accessorKey: 'startTime',
                size: 130,
                Cell: ({ cell }) => {
                    const date = cell.getValue<Date>();
                    return new Date(date).toLocaleDateString();
                },
            },

            {
                header: 'Service',
                accessorKey: 'serviceNames',
                size: 130,
                Cell: ({ cell }) => {
                    const services = cell.getValue<string[]>();
                    return services.join(', ');
                },
            },

            {
                header: 'Attendace',
                accessorKey: 'attendance',
                size: 130
            }
        ]

        return [actionColumn, ...dynamicColumns];
    }, [data]);

    const table = useMaterialReactTable({
        columns,
        initialState: { pagination: { pageIndex: 0, pageSize: 5 } },
        data: tableData,
        enableColumnActions: false,
        enableColumnFilters: true,
        enablePagination: true,
        enableSorting: true,
        enableTopToolbar: false,
        enableToolbarInternalActions: false,
        layoutMode: "grid-no-grow",
        muiTableBodyCellProps: () => {
            // set font to 18
            return {
                sx: {
                    fontSize: 12,
                },
            }
        },
        muiTableHeadCellProps: () => {
            // set font to 18
            return {
                sx: {
                    fontSize: 14,
                },
            }
        },
        muiTableBodyRowProps: ({ row }) => {
            const handleMouseUp = (event: React.MouseEvent<HTMLTableRowElement>) => {

                // Navigate to the detail page if no selection
                // navigate('/member/detail', { state: { member: row.original } });
                setCurrentRow(row.original);
                setIsViewing(true);
            };


            return {
                onMouseUp: handleMouseUp
            };
        }
    });

    return (
        <Box style={{ width: '100%' }}>
            <MaterialReactTable table={table} />

            <BookingHistoryDialog
                open={isViewing}
                onClose={() => setIsViewing(false)}
                event={currentRow}
            />
        </Box >
    )
};

